import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookOpen, CheckCircle2, Loader2, Plus, Trash2, Upload } from "lucide-react";

import { useToast } from "../../components/ui/toast";
import { cn } from "../../lib/utils";
import {
  createKnowledgeBase,
  deleteDocument,
  getDocument,
  getKnowledgeBase,
  listKnowledgeBases,
  pairsToText,
  publishKnowledgeBase,
  textToPairs,
  updateDocument,
  uploadDocument,
  type KnowledgeDocument,
} from "./api";

const KB_LIST_KEY = ["knowledge-bases"];
const kbKey = (kbId: string) => ["knowledge-bases", kbId];
const input = "w-full rounded-md border border-line bg-soft px-3 py-2 text-xs outline-none focus:border-brand";
const button = "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-bold disabled:opacity-50";

function StatusBadge({ document }: { document: KnowledgeDocument }) {
  if (document.status === "extracting") {
    return <span className="inline-flex items-center gap-1 text-ink-3"><Loader2 className="h-3 w-3 animate-spin" />Extracting</span>;
  }
  if (document.status === "failed") return <span className="text-danger" title={document.error || ""}>Failed</span>;
  return document.reviewed
    ? <span className="inline-flex items-center gap-1 text-success"><CheckCircle2 className="h-3 w-3" />Reviewed</span>
    : <span className="text-warning">Needs review</span>;
}

function UploadForm({ kbId, onUploaded }: { kbId: string; onUploaded: () => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [metaText, setMetaText] = useState("");
  // Remounts the file input after an upload; a file input can't be cleared through state.
  const [fileInputKey, setFileInputKey] = useState(0);
  const upload = useMutation({
    mutationFn: () => uploadDocument(kbId, file as File, title, textToPairs(metaText)),
    onSuccess: () => {
      setFile(null);
      setTitle("");
      setFileInputKey((key) => key + 1);
      onUploaded();
    },
    onError: (error) => toast.danger("Upload failed", (error as Error).message),
  });

  return (
    <div className="grid gap-3 rounded-md border border-line bg-soft p-4 md:grid-cols-3">
      <label className="text-[11px] font-bold text-ink-2">
        File (PDF, .txt, .md, up to 25 MB)
        <input key={fileInputKey} type="file" accept=".pdf,.txt,.md" className="mt-1.5 block w-full text-xs" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </label>
      <label className="text-[11px] font-bold text-ink-2">
        Title
        <input className={cn(input, "mt-1.5")} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Splendor+ 2023 owner's manual" />
      </label>
      <label className="text-[11px] font-bold text-ink-2">
        Tags agents can filter on, one per line
        <textarea rows={2} className={cn(input, "mt-1.5 font-mono")} value={metaText} onChange={(e) => setMetaText(e.target.value)} placeholder="model: splendor-2023" />
      </label>
      <div className="md:col-span-3">
        <button type="button" disabled={!file || upload.isPending} onClick={() => upload.mutate()} className={cn(button, "bg-brand text-white")}>
          {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload
        </button>
      </div>
    </div>
  );
}

/** Page-by-page check of the extracted text; tables come through as `a | b | c` rows. */
function DocumentReview({ kbId, docId, onClose }: { kbId: string; docId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: document } = useQuery({ queryKey: ["knowledge-document", docId], queryFn: () => getDocument(kbId, docId) });
  const [pages, setPages] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [metaText, setMetaText] = useState("");
  useEffect(() => {
    if (!document) return;
    setPages(document.pages || []);
    setMetaText(pairsToText(document.meta));
  }, [document]);

  const save = useMutation({
    mutationFn: (reviewed: boolean) => updateDocument(kbId, docId, { pages, meta: textToPairs(metaText), reviewed }),
    onSuccess: (_, reviewed) => {
      queryClient.invalidateQueries({ queryKey: kbKey(kbId) });
      queryClient.invalidateQueries({ queryKey: ["knowledge-document", docId] });
      toast.success(reviewed ? "Marked reviewed" : "Saved");
      if (reviewed) onClose();
    },
    onError: (error) => toast.danger("Could not save", (error as Error).message),
  });

  if (!document) return <Loader2 className="h-4 w-4 animate-spin text-ink-3" />;
  return (
    <div className="rounded-md border border-brand/40 bg-elevated p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-ink">Review: {document.title}</h3>
          <p className="mt-1 text-xs text-ink-3">Check that numbers and tables came through. Agents only answer from this text.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button type="button" className={cn(button, "border border-line")} disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</button>
          <span className="font-mono">Page {page + 1} / {pages.length}</span>
          <button type="button" className={cn(button, "border border-line")} disabled={page >= pages.length - 1} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </div>
      <textarea
        rows={18}
        className={cn(input, "mt-3 font-mono leading-5")}
        value={pages[page] ?? ""}
        onChange={(e) => setPages(pages.map((text, i) => (i === page ? e.target.value : text)))}
      />
      <label className="mt-3 block text-[11px] font-bold text-ink-2">
        Tags, one per line
        <textarea rows={2} className={cn(input, "mt-1.5 font-mono")} value={metaText} onChange={(e) => setMetaText(e.target.value)} />
      </label>
      <div className="mt-3 flex gap-2">
        <button type="button" className={cn(button, "border border-line")} disabled={save.isPending} onClick={() => save.mutate(false)}>Save</button>
        <button type="button" className={cn(button, "bg-brand text-white")} disabled={save.isPending} onClick={() => save.mutate(true)}>Save and mark reviewed</button>
        <button type="button" className={cn(button, "ml-auto text-ink-3")} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function KnowledgeBaseDetail({ kbId }: { kbId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [reviewing, setReviewing] = useState<string | null>(null);
  const { data: kb } = useQuery({
    queryKey: kbKey(kbId),
    queryFn: () => getKnowledgeBase(kbId),
    // Extraction runs in the background on core; poll until it settles.
    refetchInterval: (query) => (query.state.data?.documents?.some((d) => d.status === "extracting") ? 3000 : false),
  });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: kbKey(kbId) });
    queryClient.invalidateQueries({ queryKey: KB_LIST_KEY });
  };
  const publish = useMutation({
    mutationFn: () => publishKnowledgeBase(kbId),
    onSuccess: (version) => {
      toast.success("Published", `${version.chunk_count} passage${version.chunk_count === 1 ? "" : "s"}. New calls use it straight away.`);
      refresh();
    },
    onError: (error) => toast.danger("Could not publish", (error as Error).message),
  });
  const remove = useMutation({
    mutationFn: (docId: string) => deleteDocument(kbId, docId),
    onSuccess: refresh,
    onError: (error) => toast.danger("Could not delete", (error as Error).message),
  });

  if (!kb) return <Loader2 className="h-5 w-5 animate-spin text-ink-3" />;
  const documents = kb.documents || [];
  const publishable = documents.length > 0 && documents.every((d) => d.status === "ready" && d.reviewed);
  const unpublished = documents.some((d) => !kb.published_at || d.updated_at > kb.published_at);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-ink">{kb.name}</h2>
          <p className="mt-1 text-xs text-ink-3">
            {kb.active_version ? <>Live version <span className="font-mono">{kb.active_version}</span></> : "Not published yet"}
            {kb.active_version && unpublished && " · has unpublished changes"}
          </p>
        </div>
        <button type="button" disabled={!publishable || publish.isPending} onClick={() => publish.mutate()} className={cn(button, "bg-brand text-white")}
          title={publishable ? "" : "Every document must be extracted and reviewed"}>
          {publish.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Publish
        </button>
      </div>

      <UploadForm kbId={kbId} onUploaded={refresh} />

      <table className="w-full text-left text-xs">
        <thead className="text-[11px] text-ink-3">
          <tr><th className="py-2">Document</th><th>Tags</th><th>Pages</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {documents.map((document) => (
            <tr key={document.doc_id} className="border-t border-line">
              <td className="py-2 font-bold text-ink">{document.title}</td>
              <td className="font-mono text-ink-2">{pairsToText(document.meta) || "—"}</td>
              <td>{document.page_count || "—"}</td>
              <td><StatusBadge document={document} /></td>
              <td className="text-right">
                {document.status === "ready" && (
                  <button type="button" className={cn(button, "text-brand")} onClick={() => setReviewing(document.doc_id)}>Review</button>
                )}
                <button type="button" aria-label="Delete document" className={cn(button, "text-ink-3")} onClick={() => remove.mutate(document.doc_id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
          {!documents.length && <tr><td colSpan={5} className="py-6 text-center text-ink-3">Upload a manual, policy or price list to start.</td></tr>}
        </tbody>
      </table>

      {reviewing && <DocumentReview kbId={kbId} docId={reviewing} onClose={() => setReviewing(null)} />}

      {!!kb.versions?.length && (
        <div>
          <h3 className="text-xs font-bold text-ink">Published versions</h3>
          <ul className="mt-2 space-y-1 text-xs text-ink-2">
            {kb.versions.map((v) => (
              <li key={v.version} className="font-mono">{v.version} · {v.chunk_count} passages · {new Date(v.published_at).toLocaleString()}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function KnowledgePage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState("");
  const { data: kbs = [], isLoading } = useQuery({ queryKey: KB_LIST_KEY, queryFn: listKnowledgeBases });
  const create = useMutation({
    mutationFn: () => createKnowledgeBase(name.trim()),
    onSuccess: (kb) => {
      setName("");
      setSelected(kb.kb_id);
      queryClient.invalidateQueries({ queryKey: KB_LIST_KEY });
    },
    onError: (error) => toast.danger("Could not create", (error as Error).message),
  });
  const active = selected ?? kbs[0]?.kb_id ?? null;

  return (
    <div className="grid min-h-full gap-4 p-4 lg:grid-cols-[260px_1fr]">
      <aside className="space-y-3 rounded-md border border-line bg-elevated p-4 shadow-sm">
        <h1 className="flex items-center gap-2 text-sm font-bold text-ink"><BookOpen className="h-4 w-4" />Knowledge</h1>
        <p className="text-xs leading-5 text-ink-3">Documents your agents can search during a call. Attach one to an agent in its Settings.</p>
        <div className="flex gap-2">
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="New knowledge base" />
          <button type="button" aria-label="Create knowledge base" disabled={name.trim().length < 2 || create.isPending} onClick={() => create.mutate()} className={cn(button, "bg-brand text-white")}>
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-ink-3" />}
        <ul className="space-y-1">
          {kbs.map((kb) => (
            <li key={kb.kb_id}>
              <button type="button" onClick={() => setSelected(kb.kb_id)}
                className={cn("w-full rounded-md px-3 py-2 text-left text-xs", kb.kb_id === active ? "bg-soft font-bold text-ink" : "text-ink-2 hover:bg-soft")}>
                {kb.name}
                <span className="block text-[10px] font-normal text-ink-3">{kb.active_version ? "Published" : "Draft"}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <main className="rounded-md border border-line bg-elevated p-5 shadow-sm">
        {active ? <KnowledgeBaseDetail key={active} kbId={active} /> : <p className="text-xs text-ink-3">Create a knowledge base to begin.</p>}
      </main>
    </div>
  );
}
